/**
 * Which `Delivery Location` to create an enquiry with.
 *
 * `create_enquiry` will not create a document without one — measured live:
 * `MandatoryError: [Enquiry, …]: delivery_location`, raised even when the customer is valid. And
 * it is a Link to a 10-row master, not free text, so an unrecognised place name cannot be sent:
 * it fails Frappe's Link validation for the WHOLE document, not just its own field.
 *
 * So: scan the mail for a location the master actually has, and fall back when there is none.
 * `matched` tells the caller which happened, so the UI can say "we guessed" rather than implying
 * the mail specified it.
 */

/**
 * Used when the mail names no location we recognise.
 *
 * **This is a stand-in, not a business rule.** There is no `Bosun Settings.default_delivery_location`
 * to read — filed as an ask. `UAE` because it is what the live bench overwhelmingly uses (3 of the
 * last 4 enquiries). It is acceptable only because the confirmation bar always shows it BEFORE the
 * write and the RFQ header flags it after, so it is never applied silently.
 */
export const FALLBACK_DELIVERY_LOCATION = 'UAE'

/**
 * The earliest master location named in `text`, or the fallback.
 *
 * Word-boundary matched, deliberately: a plain substring search finds `OMAN` inside `ROMANIA` and
 * would route a Romanian shipment to Oman.
 */
export function pickDeliveryLocation(text: string, master: string[]): { location: string; matched: boolean } {
  let best: { location: string; at: number } | null = null

  for (const location of master) {
    // The master holds plain place names, but escape anyway — a name with a regex metacharacter
    // would otherwise build a broken or over-matching pattern.
    const escaped = location.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const at = text.search(new RegExp(`\\b${escaped}\\b`, 'i'))
    if (at === -1) continue
    if (!best || at < best.at) best = { location, at }
  }

  return best ? { location: best.location, matched: true } : { location: FALLBACK_DELIVERY_LOCATION, matched: false }
}
